import type { Editor, FrameSource } from '@aitomol/core';
import { TrajectoryPlayer } from '@aitomol/anim';

/**
 * Playback bridge between the editor store and `TrajectoryPlayer`.
 *
 * The LightCone skin exposes Play/Prev/Next actions that only write
 * `state.playing` / call `setFrame`; nothing in the toolkit turns `playing`
 * into advancing frames unless a `TrajectoryPlayer` is attached. This class owns
 * that player, keeps it pointed at the document's own frames (optimisation
 * trajectories, NMR models, multi-frame XYZ), and swaps in a vibration frame
 * source without losing the original.
 *
 * Re-entrancy is the one real hazard: `attach()` writes to the store, which
 * notifies this listener again. `attached` is therefore updated *before* the
 * player is attached, and `syncing` guards the nested notification.
 */
export class PlatformPlayback {
    private readonly player: TrajectoryPlayer;
    private readonly editor: Editor;
    private attached: FrameSource | null | undefined = undefined;
    private documentFrames: FrameSource | null = null;
    private vibrationFrames: FrameSource | null = null;
    private unsubscribe: (() => void) | null = null;
    private syncing = false;

    constructor(editor: Editor, options: { fps?: number; loop?: boolean } = {}) {
        this.editor = editor;
        this.player = new TrajectoryPlayer(editor, { fps: options.fps ?? 12, loop: options.loop ?? true });
        this.unsubscribe = editor.store.subscribe(() => this.sync());
        this.sync();
    }

    get frame(): number {
        return this.editor.state.frame;
    }

    get frameCount(): number {
        return this.editor.state.frameCount;
    }

    get playing(): boolean {
        return this.editor.state.playing;
    }

    get animatingVibration(): boolean {
        return this.vibrationFrames !== null;
    }

    label(frame = this.frame): string | undefined {
        return this.player.label(frame);
    }

    play(): void {
        this.player.play();
        this.editor.store.setState({ playing: true });
    }

    pause(): void {
        this.player.pause();
        this.editor.store.setState({ playing: false });
    }

    toggle(): void {
        if (this.playing) this.pause();
        else this.play();
    }

    seek(frame: number): void {
        this.player.seek(frame);
    }

    step(delta: number): void {
        this.player.step(delta);
    }

    setFps(fps: number): void {
        this.player.setFps(fps);
    }

    setLoop(loop: boolean): void {
        this.player.setLoop(loop);
    }

    /** Frames that came from the loaded document (null for single-frame files). */
    get documentSource(): FrameSource | null {
        return this.documentFrames;
    }

    /** Replaces the timeline with a generated source (vibration, morph, ...). */
    startGenerated(source: FrameSource): void {
        this.vibrationFrames = source;
        this.attach(source);
        this.player.play();
        this.editor.store.setState({ playing: true });
    }

    /** Restores the document's own frames after `startGenerated`. */
    stopGenerated(): void {
        if (!this.vibrationFrames) return;
        this.vibrationFrames = null;
        this.player.pause();
        this.attach(this.documentFrames);
        this.editor.store.setState({ playing: false, frame: Math.min(this.editor.state.frame, Math.max(0, this.frameCount - 1)) });
    }

    private attach(source: FrameSource | null): void {
        // Mark first: attach() writes state.frames, and sync() must treat that
        // write as already handled instead of attaching a second time.
        this.attached = source;
        this.player.attach(source);
    }

    private sync(): void {
        if (this.syncing) return;
        this.syncing = true;
        try {
            const state = this.editor.state;
            if (state.frames !== this.attached) {
                const external = state.frames !== this.vibrationFrames;
                if (external) {
                    this.vibrationFrames = null;
                    this.documentFrames = state.frames;
                }
                this.attach(state.frames);
            }
            if (state.playing !== this.player.playing) {
                if (state.playing) this.player.play();
                else this.player.pause();
            }
        } finally {
            this.syncing = false;
        }
    }

    dispose(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.player.dispose();
        this.attached = undefined;
        this.documentFrames = null;
        this.vibrationFrames = null;
    }
}